import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Container,
  Card,
  Grid,
  CardContent,
} from "@mui/material";
import { Edit, Delete, Add, TrendingUp, TrendingDown, AccountBalance, FileDownload } from "@mui/icons-material";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";
import { motion } from "framer-motion";
import { FaMoneyBillWave, FaChartLine } from "react-icons/fa";
import * as XLSX from "xlsx";

const Accounts = () => {
  const [items, setItems] = useState([]);
  const [incomeName, setIncomeName] = useState("");
  const [incomeAmount, setIncomeAmount] = useState("");
  const [incomeQuantity, setIncomeQuantity] = useState("");
  const [expenditureName, setExpenditureName] = useState("");
  const [expenditurePrice, setExpenditurePrice] = useState("");
  const [expenditureQuantity, setExpenditureQuantity] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [openIncomeDialog, setOpenIncomeDialog] = useState(false);
  const [openExpenditureDialog, setOpenExpenditureDialog] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const snapshot = await firestore.collection("accounting").get();
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setItems(data);
      } catch (error) {
        console.error("Error fetching items: ", error);
      }
    };
    fetchItems();
  }, []);

  const handleIncomeSubmit = async () => {
    if (!incomeName || !incomeAmount || !incomeQuantity) return;
    const newItem = {
      type: "income",
      name: incomeName,
      amount: parseFloat(incomeAmount),
      quantity: parseInt(incomeQuantity),
      total: parseFloat(incomeAmount) * parseInt(incomeQuantity),
    };

    try {
      const docRef = await firestore.collection("accounting").add(newItem);
      setItems([...items, { id: docRef.id, ...newItem }]);
      setIncomeName("");
      setIncomeAmount("");
      setIncomeQuantity("");
      setOpenIncomeDialog(false);
    } catch (error) {
      console.error("Error saving income: ", error);
    }
  };

  const handleExpenditureSubmit = async () => {
    if (!expenditureName || !expenditurePrice || !expenditureQuantity) return;
    const newItem = {
      type: "expenditure",
      name: expenditureName,
      price: parseFloat(expenditurePrice),
      quantity: parseInt(expenditureQuantity),
      total: -Math.abs(parseFloat(expenditurePrice) * parseInt(expenditureQuantity)),
    };

    try {
      const docRef = await firestore.collection("accounting").add(newItem);
      setItems([...items, { id: docRef.id, ...newItem }]);
      setExpenditureName("");
      setExpenditurePrice("");
      setExpenditureQuantity("");
      setOpenExpenditureDialog(false);
    } catch (error) {
      console.error("Error saving expenditure: ", error);
    }
  };

  const handleEdit = (item) => {
    if (item.type === "income") {
      setIncomeName(item.name);
      setIncomeAmount(item.amount.toString());
      setIncomeQuantity(item.quantity.toString());
      setOpenIncomeDialog(true);
    } else {
      setExpenditureName(item.name);
      setExpenditurePrice(item.price.toString());
      setExpenditureQuantity(item.quantity.toString());
      setOpenExpenditureDialog(true);
    }
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await firestore.collection("accounting").doc(id).delete();
      setItems(items.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting item: ", error);
    }
  };

  const totalIncome = items.filter(i => i.type === "income").reduce((sum, item) => sum + item.total, 0);
  const totalExpenditure = Math.abs(items.filter(i => i.type === "expenditure").reduce((sum, item) => sum + item.total, 0));
  const totalBalance = items.reduce((sum, item) => sum + item.total, 0);

  const paginatedItems = items.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const exportToExcel = () => {
    const worksheetData = items.map((item) => ({
      Type: item.type,
      Name: item.name,
      Price: item.price ? item.price.toFixed(2) : item.amount.toFixed(2),
      Quantity: item.quantity,
      Total: item.total.toFixed(2),
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Accounting");
    XLSX.writeFile(workbook, "ICTAZ_Accounting.xlsx");
  };

  const statCards = [
    { title: "Total Income", value: `K${totalIncome.toFixed(2)}`, icon: <TrendingUp sx={{ fontSize: 30 }} />, color: "#10b981" },
    { title: "Total Expenditure", value: `K${totalExpenditure.toFixed(2)}`, icon: <TrendingDown sx={{ fontSize: 30 }} />, color: "#ef4444" },
    { title: "Balance", value: `K${totalBalance.toFixed(2)}`, icon: <AccountBalance sx={{ fontSize: 30 }} />, color: totalBalance >= 0 ? "#00d9ff" : "#ef4444" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "15px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaMoneyBillWave size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>Accounting Module</Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>Track income and expenditure</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ y: -5 }}>
                <Card sx={{ borderRadius: "20px", ...darkGlassStyle, "&:hover": { border: "1px solid rgba(0, 217, 255, 0.5)" } }}>
                  <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 3 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 1 }}>{stat.title}</Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>{stat.value}</Typography>
                    </Box>
                    <Box sx={{ width: 60, height: 60, borderRadius: "15px", background: `${stat.color}20`, border: `2px solid ${stat.color}`, display: "flex", alignItems: "center", justifyContent: "center", color: stat.color }}>
                      {stat.icon}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Action Buttons */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Button onClick={() => setOpenIncomeDialog(true)} variant="contained" startIcon={<Add />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" } }}>
              Add Income
            </Button>
            <Button onClick={() => setOpenExpenditureDialog(true)} variant="contained" startIcon={<Add />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #ef4444, #dc2626)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #dc2626, #b91c1c)" } }}>
              Add Expenditure
            </Button>
            <Button onClick={exportToExcel} variant="contained" startIcon={<FileDownload />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" } }}>
              Export to Excel
            </Button>
          </Box>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(0, 180, 216, 0.1))" }}>
                    {["Type", "Name", "Price/Amount", "Quantity", "Total", "Actions"].map((header) => (
                      <TableCell key={header} sx={{ color: "#00d9ff", fontWeight: 700, fontSize: "15px", borderBottom: "1px solid rgba(0, 217, 255, 0.2)" }}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.map((item) => (
                    <TableRow key={item.id} sx={{ "&:hover": { background: "rgba(0, 217, 255, 0.05)" }, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: item.type === "income" ? "#10b981" : "#ef4444" }} />
                          <Typography sx={{ color: item.type === "income" ? "#10b981" : "#ef4444", fontWeight: 600, textTransform: "capitalize" }}>{item.type}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "white" }}>{item.name}</TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>K{item.price ? item.price.toFixed(2) : item.amount.toFixed(2)}</TableCell>
                      <TableCell sx={{ color: "rgba(255,255,255,0.8)" }}>{item.quantity}</TableCell>
                      <TableCell sx={{ color: item.total >= 0 ? "#10b981" : "#ef4444", fontWeight: 600 }}>K{item.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => handleEdit(item)}
                            sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(16, 185, 129, 0.2)", border: "1px solid #10b981", color: "#10b981", "&:hover": { background: "rgba(16, 185, 129, 0.3)" } }}>
                            <Edit fontSize="small" />
                          </Button>
                          <Button size="small" onClick={() => handleDelete(item.id)}
                            sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(239, 68, 68, 0.2)", border: "1px solid #ef4444", color: "#ef4444", "&:hover": { background: "rgba(239, 68, 68, 0.3)" } }}>
                            <Delete fontSize="small" />
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}>No entries yet. Start by adding income or expenditure.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid rgba(0, 217, 255, 0.2)" }}>
              <Pagination count={Math.ceil(items.length / rowsPerPage)} page={page} onChange={(e, v) => setPage(v)}
                sx={{ "& .MuiPaginationItem-root": { color: "white", "&.Mui-selected": { background: "#00d9ff", color: "#000024" } } }} />
            </Box>
          </Card>
        </motion.div>

        {/* Income Dialog */}
        <Dialog open={openIncomeDialog} onClose={() => setOpenIncomeDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TrendingUp sx={{ color: "white" }} />
            </Box>
            Add Income
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
              <TextField label="Income Name" value={incomeName} onChange={(e) => setIncomeName(e.target.value)} fullWidth sx={inputStyle} />
              <TextField label="Amount (K)" type="number" value={incomeAmount} onChange={(e) => setIncomeAmount(e.target.value)} fullWidth sx={inputStyle} />
              <TextField label="Quantity" type="number" value={incomeQuantity} onChange={(e) => setIncomeQuantity(e.target.value)} fullWidth sx={inputStyle} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(0, 217, 255, 0.2)" }}>
            <Button onClick={() => setOpenIncomeDialog(false)} sx={{ color: "rgba(255,255,255,0.7)" }}>Cancel</Button>
            <Button onClick={handleIncomeSubmit} variant="contained"
              sx={{ borderRadius: "10px", background: "linear-gradient(135deg, #10b981, #059669)", "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" } }}>
              Add Income
            </Button>
          </DialogActions>
        </Dialog>

        {/* Expenditure Dialog */}
        <Dialog open={openExpenditureDialog} onClose={() => setOpenExpenditureDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #ef4444, #dc2626)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TrendingDown sx={{ color: "white" }} />
            </Box>
            Add Expenditure
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
              <TextField label="Expenditure Name" value={expenditureName} onChange={(e) => setExpenditureName(e.target.value)} fullWidth sx={inputStyle} />
              <TextField label="Price (K)" type="number" value={expenditurePrice} onChange={(e) => setExpenditurePrice(e.target.value)} fullWidth sx={inputStyle} />
              <TextField label="Quantity" type="number" value={expenditureQuantity} onChange={(e) => setExpenditureQuantity(e.target.value)} fullWidth sx={inputStyle} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(0, 217, 255, 0.2)" }}>
            <Button onClick={() => setOpenExpenditureDialog(false)} sx={{ color: "rgba(255,255,255,0.7)" }}>Cancel</Button>
            <Button onClick={handleExpenditureSubmit} variant="contained"
              sx={{ borderRadius: "10px", background: "linear-gradient(135deg, #ef4444, #dc2626)", "&:hover": { background: "linear-gradient(135deg, #dc2626, #b91c1c)" } }}>
              Add Expenditure
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Accounts;
